/**
 * @page FemaleHealth
 * @route /female-health (auth)
 * @description Female Health Hub — "the most premium section of the app"
 *
 * Yuri spec 2026-02 Iter. 312 — Mauve/Fuchsia Glow signature.
 * Visible only to users with biological_sex='femme' (gated at server too).
 *
 * Sections:
 *  1. Cycle phase live indicator (Folliculaire / Ovulation / Lutéale / Menstrual)
 *  2. Live tracking — daily energy + symptoms + period day toggle
 *     - Editable for any past day (calendar grid for current month + month picker)
 *  3. Cross chart: cycle day × discipline score (last 30 days)
 *  4. Educational tabs: Cycle Régulier · Périménopause · Ménopause
 *  5. AI Insight ribbon (uses cycle.phase to suggest tactical adjustment)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, ChevronLeft, ChevronRight, Droplet, Flame,
  Activity, Brain, Dumbbell, Heart, Sun, Moon, Zap,
  CalendarDays, Pencil, Check, Save, X,
  Apple, Bone, Egg, Coffee, ShieldCheck, Clock, Salad, Wheat,
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { api, useLanguage } from '../context/AppContext';

// ─── Constants ───
const SYMPTOMS = [
  { key: 'water_retention', icon: Droplet,   fr: "Rétention d'eau", en: 'Water retention' },
  { key: 'fatigue',         icon: Moon,      fr: 'Fatigue',         en: 'Fatigue' },
  { key: 'sugar_cravings',  icon: Flame,     fr: 'Rage de sucre',   en: 'Sugar cravings' },
  { key: 'cramps',          icon: Activity,  fr: 'Crampes',         en: 'Cramps' },
  { key: 'bloating',        icon: Sun,       fr: 'Ballonnements',   en: 'Bloating' },
  { key: 'mood_swing',      icon: Heart,     fr: "Sautes d'humeur", en: 'Mood swings' },
  { key: 'headache',        icon: Brain,     fr: 'Maux de tête',    en: 'Headache' },
];

const PHASE_PALETTE = {
  menstrual:     { color: '#f43f5e', label_fr: 'Menstruelle',  label_en: 'Menstrual',  icon: Droplet },
  follicular:    { color: '#a78bfa', label_fr: 'Folliculaire', label_en: 'Follicular', icon: Sun },
  ovulation:     { color: '#f472b6', label_fr: 'Ovulation',    label_en: 'Ovulation',  icon: Sparkles },
  luteal:        { color: '#c084fc', label_fr: 'Lutéale',      label_en: 'Luteal',     icon: Moon },
  pre_menstrual: { color: '#e879f9', label_fr: 'Pré-menstruelle', label_en: 'Pre-menstrual', icon: Activity },
};

// ─── Utilities ───
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function daysInMonthGrid(d) {
  // Returns an array of Date objects covering the full month grid (Mon..Sun rows).
  const first = startOfMonth(d);
  const last = endOfMonth(d);
  const startWeekday = (first.getDay() + 6) % 7; // Mon=0
  const total = startWeekday + last.getDate();
  const rows = Math.ceil(total / 7) * 7;
  const arr = [];
  for (let i = 0; i < rows; i++) {
    const day = new Date(first);
    day.setDate(first.getDate() - startWeekday + i);
    arr.push(day);
  }
  return arr;
}

// One-shot keyframe injection for the female-health page (idempotent).
// We can't use Tailwind's built-in animations because we need bespoke
// shimmer + pulse for the "Log today" CTA.
let _fhKeyframesInjected = false;
function injectFemaleHealthKeyframes() {
  if (typeof document === 'undefined' || _fhKeyframesInjected) return;
  _fhKeyframesInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-fh-keyframes', '1');
  style.textContent = `
    @keyframes fh-log-shimmer {
      0%   { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
    @keyframes fh-log-pulse {
      0%, 100% { box-shadow: 0 0 28px rgba(232,121,249,0.85), 0 0 10px rgba(192,132,252,0.75), inset 0 0 0 1px rgba(255,255,255,0.18); }
      50%      { box-shadow: 0 0 44px rgba(232,121,249,1.0),  0 0 16px rgba(192,132,252,0.95), inset 0 0 0 1px rgba(255,255,255,0.30); }
    }
  `;
  document.head.appendChild(style);
}

// ─── Atoms ───
function GlowCard({ children, accent = '#c084fc', testId, className = '' }) {
  return (
    <div data-testid={testId}
      className={`relative rounded-2xl bg-gradient-to-br from-zinc-950/85 via-black/80 to-zinc-950/70 ${className}`}
      style={{
        border: `1px solid ${accent}66`,
        boxShadow: `0 0 22px ${accent}33, inset 0 0 0 1px ${accent}1A`,
      }}>
      {children}
    </div>
  );
}

function MauveLabel({ children, color = '#c084fc', testId }) {
  return (
    <div data-testid={testId}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-mono"
      style={{ background: `${color}1A`, border: `1px solid ${color}66`, boxShadow: `0 0 10px ${color}55` }}>
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: color }} />
        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
      </span>
      <span className="text-[10px] font-black tracking-[0.28em] uppercase" style={{ color, textShadow: `0 0 8px ${color}99` }}>
        {children}
      </span>
    </div>
  );
}

// ─── Phase indicator ───
function PhaseRing({ cycle, fr }) {
  if (!cycle?.active) {
    return (
      <GlowCard testId="phase-ring-inactive" className="p-6">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {fr ? "Cycle non configuré. Va dans les Réglages → Profil hormonal pour activer le suivi (sexe biologique = femme, phase de vie = cycle actif, dernière date des règles)."
              : 'Cycle not configured. Go to Settings → Hormonal profile to enable tracking.'}
        </p>
      </GlowCard>
    );
  }
  const meta = PHASE_PALETTE[cycle.phase] || PHASE_PALETTE.follicular;
  const Icon = meta.icon;
  const total = cycle.cycle_duration || 28;
  const day = cycle.cycle_day || 1;
  const pct = Math.min(100, (day / total) * 100);
  return (
    <GlowCard testId="phase-ring" className="p-5 sm:p-6" accent={meta.color}>
      <div className="flex items-start gap-4">
        <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0">
          {/* Outer ring */}
          <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
            <circle cx="50" cy="50" r="44" fill="none" stroke={`${meta.color}22`} strokeWidth="6" />
            <circle cx="50" cy="50" r="44" fill="none" stroke={meta.color} strokeWidth="6"
              strokeLinecap="round" strokeDasharray={`${(pct * 2 * Math.PI * 44) / 100} ${2 * Math.PI * 44}`}
              style={{ filter: `drop-shadow(0 0 6px ${meta.color})` }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Icon className="w-5 h-5" style={{ color: meta.color, filter: `drop-shadow(0 0 6px ${meta.color})` }} />
            <span className="text-[10px] font-mono tracking-widest text-muted-foreground mt-0.5">J{day}</span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1" style={{ color: meta.color, textShadow: `0 0 6px ${meta.color}99` }}>
            {fr ? 'Phase actuelle' : 'Current phase'}
          </p>
          <h3 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight font-black text-foreground mb-1">
            {fr ? meta.label_fr : meta.label_en}
          </h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {fr ? cycle.phase_desc_fr : cycle.phase_desc_en}
          </p>
          {cycle.next_period_date && (
            <p className="mt-2 text-[11px] font-mono text-muted-foreground/80">
              {fr ? 'Prochaine phase menstruelle dans' : 'Next period in'}{' '}
              <span className="font-black" style={{ color: meta.color }}>{cycle.days_until_period}j</span>
            </p>
          )}
        </div>
      </div>
    </GlowCard>
  );
}

// ─── Day editor modal ───
function DayLogEditor({ open, onClose, dateStr, log, onSaved, fr }) {
  const [energy, setEnergy] = useState(log?.energy || 3);
  const [isPeriod, setIsPeriod] = useState(!!log?.is_period_day);
  const [symptoms, setSymptoms] = useState(log?.symptoms || {});
  const [note, setNote] = useState(log?.note || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnergy(log?.energy || 3);
    setIsPeriod(!!log?.is_period_day);
    setSymptoms(log?.symptoms || {});
    setNote(log?.note || '');
  }, [dateStr, log]);

  const toggle = (k) => setSymptoms((s) => ({ ...s, [k]: !s[k] }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post('/female-health/logs', {
        log_date: dateStr,
        energy,
        is_period_day: isPeriod,
        symptoms,
        note: note.slice(0, 280),
      });
      onSaved?.(res.data?.log);
      onClose?.();
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      onClick={onClose} data-testid="day-log-editor-overlay">
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-lg rounded-2xl bg-gradient-to-br from-fuchsia-950/40 via-zinc-950 to-purple-950/30 p-6"
        style={{ border: '1px solid rgba(192,132,252,0.55)', boxShadow: '0 0 28px rgba(192,132,252,0.45)' }}
        onClick={(e) => e.stopPropagation()} data-testid="day-log-editor-modal">
        <button onClick={onClose} className="absolute top-3 right-3 w-7 h-7 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center"
          data-testid="day-log-close">
          <X className="w-3.5 h-3.5 text-zinc-400" />
        </button>
        <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-fuchsia-300 mb-1"
          style={{ textShadow: '0 0 6px rgba(232,121,249,0.6)' }}>
          {fr ? 'Édition du jour' : 'Edit day'}
        </p>
        <h3 className="font-heading text-xl uppercase tracking-tight font-black text-foreground mb-5">{dateStr}</h3>

        {/* Energy 1..5 */}
        <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
          {fr ? 'Énergie' : 'Energy'}
        </p>
        <div className="flex gap-2 mb-5" data-testid="day-log-energy">
          {[1, 2, 3, 4, 5].map((v) => (
            <button key={v} type="button" onClick={() => setEnergy(v)}
              data-testid={`day-log-energy-${v}`}
              className={`flex-1 py-2 rounded-lg text-xs font-black tabular-nums transition-all ${
                energy === v
                  ? 'bg-gradient-to-br from-fuchsia-500 to-purple-500 text-white'
                  : 'bg-zinc-900/80 text-muted-foreground ring-1 ring-zinc-800 hover:ring-fuchsia-500/40'
              }`}
              style={energy === v ? { boxShadow: '0 0 14px rgba(192,132,252,0.65)' } : {}}>
              {v}
            </button>
          ))}
        </div>

        {/* Period toggle */}
        <button type="button" onClick={() => setIsPeriod((v) => !v)} data-testid="day-log-period-toggle"
          className={`w-full mb-5 px-4 py-3 rounded-xl flex items-center justify-between transition-all ${
            isPeriod
              ? 'bg-gradient-to-r from-rose-600/30 to-fuchsia-600/30 ring-2 ring-rose-400/60'
              : 'bg-zinc-900/80 ring-1 ring-zinc-800 hover:ring-rose-500/40'
          }`}
          style={isPeriod ? { boxShadow: '0 0 16px rgba(244,63,94,0.45)' } : {}}>
          <span className="flex items-center gap-2.5">
            <Droplet className={`w-4 h-4 ${isPeriod ? 'text-rose-300' : 'text-muted-foreground'}`} />
            <span className={`text-xs uppercase tracking-widest font-bold ${isPeriod ? 'text-rose-200' : 'text-muted-foreground'}`}>
              {fr ? 'Jour de règles' : 'Period day'}
            </span>
          </span>
          {isPeriod && <Check className="w-4 h-4 text-rose-300" />}
        </button>

        {/* Symptoms grid */}
        <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
          {fr ? 'Symptômes' : 'Symptoms'}
        </p>
        <div className="grid grid-cols-2 gap-2 mb-5" data-testid="day-log-symptoms">
          {SYMPTOMS.map((s) => {
            const SI = s.icon;
            const on = !!symptoms[s.key];
            return (
              <button key={s.key} type="button" onClick={() => toggle(s.key)} data-testid={`symptom-${s.key}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                  on
                    ? 'bg-gradient-to-r from-fuchsia-600/25 to-purple-600/25 ring-1 ring-fuchsia-400/55'
                    : 'bg-zinc-900/70 ring-1 ring-zinc-800 hover:ring-fuchsia-500/35'
                }`}
                style={on ? { boxShadow: '0 0 12px rgba(232,121,249,0.4)' } : {}}>
                <SI className={`w-3.5 h-3.5 flex-shrink-0 ${on ? 'text-fuchsia-300' : 'text-muted-foreground'}`} />
                <span className={`text-[11px] font-bold ${on ? 'text-fuchsia-200' : 'text-muted-foreground'}`}>
                  {fr ? s.fr : s.en}
                </span>
              </button>
            );
          })}
        </div>

        {/* Note */}
        <textarea data-testid="day-log-note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 280))}
          placeholder={fr ? 'Note libre (max 280 caractères)' : 'Free note (max 280 chars)'}
          className="w-full px-3 py-2 rounded-lg bg-zinc-950/70 border border-zinc-800 text-sm text-foreground placeholder-muted-foreground/60 focus:border-fuchsia-500/60 focus:outline-none resize-none mb-5"
          rows={2} />

        <Button disabled={saving} onClick={save} data-testid="day-log-save"
          className="w-full bg-gradient-to-r from-fuchsia-500 to-purple-500 hover:from-fuchsia-400 hover:to-purple-400 text-white font-black uppercase tracking-widest"
          style={{ boxShadow: '0 0 18px rgba(192,132,252,0.55)' }}>
          {saving ? (fr ? 'Enregistrement…' : 'Saving…') : (<><Save className="w-4 h-4 mr-2" />{fr ? 'Enregistrer' : 'Save'}</>)}
        </Button>
      </motion.div>
    </div>
  );
}

// ─── Calendar ───
function CycleCalendar({ logsByDate, currentMonth, setCurrentMonth, onPickDay, fr, todayStr }) {
  const grid = useMemo(() => daysInMonthGrid(currentMonth), [currentMonth]);
  const monthLabel = currentMonth.toLocaleDateString(fr ? 'fr-FR' : 'en-US', { month: 'long', year: 'numeric' });
  const weekdays = fr ? ['L', 'M', 'M', 'J', 'V', 'S', 'D'] : ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return (
    <GlowCard testId="cycle-calendar" className="p-5">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
          className="w-7 h-7 rounded-full bg-zinc-900/70 hover:bg-fuchsia-500/15 flex items-center justify-center"
          data-testid="cycle-cal-prev">
          <ChevronLeft className="w-4 h-4 text-fuchsia-300" />
        </button>
        <p className="font-heading uppercase tracking-widest text-sm text-foreground" data-testid="cycle-cal-month">
          {monthLabel}
        </p>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
          className="w-7 h-7 rounded-full bg-zinc-900/70 hover:bg-fuchsia-500/15 flex items-center justify-center"
          data-testid="cycle-cal-next">
          <ChevronRight className="w-4 h-4 text-fuchsia-300" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1.5">
        {weekdays.map((wd, i) => (
          <div key={`wd-${i}`} className="text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 py-1">{wd}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d) => {
          const inMonth = d.getMonth() === currentMonth.getMonth();
          const ds = ymd(d);
          const log = logsByDate[ds];
          const hasPeriod = log?.is_period_day;
          const hasSymptoms = log && Object.values(log.symptoms || {}).some(Boolean);
          const isToday = ds === todayStr;
          const isFuture = ds > todayStr;
          return (
            <button key={ds} type="button" disabled={isFuture}
              onClick={() => !isFuture && onPickDay(ds)}
              data-testid={`cycle-cal-day-${ds}`}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-bold transition-all relative ${
                !inMonth ? 'opacity-30' : ''
              } ${isFuture ? 'cursor-not-allowed opacity-25' : 'hover:bg-fuchsia-500/15'} ${
                isToday ? 'ring-1 ring-fuchsia-400/60' : ''
              } ${hasPeriod ? 'bg-gradient-to-br from-rose-600/30 to-fuchsia-600/30 ring-1 ring-rose-400/55' : 'bg-zinc-900/40'}`}
              style={hasPeriod ? { boxShadow: '0 0 10px rgba(244,63,94,0.45)' } : {}}>
              <span className={`${hasPeriod ? 'text-rose-100' : isToday ? 'text-fuchsia-200' : 'text-foreground/85'}`}>
                {d.getDate()}
              </span>
              {hasSymptoms && !hasPeriod && (
                <span className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-fuchsia-400" style={{ boxShadow: '0 0 4px rgba(232,121,249,0.9)' }} />
              )}
            </button>
          );
        })}
      </div>
    </GlowCard>
  );
}

// ─── Cross-chart: phase × discipline ───
// When the user has no discipline_scores entries yet (fresh account), we fall
// back to the energy field from female-health logs (1-5 scaled to 0-100) so
// that the chart starts populating from the very first log.
function DisciplineXPhaseChart({ disciplineSeries, logs, fr }) {
  const periodDays = new Set(logs.filter((l) => l.is_period_day).map((l) => l.log_date));

  // Build a unified series: prefer discipline_scores when available, else
  // fallback to logged energy * 20 (1-5 scale → 20-100).
  const fallbackFromEnergy = logs
    .filter((l) => typeof l.energy === 'number')
    .map((l) => ({ date: l.log_date, score: Math.max(20, Math.min(100, l.energy * 20)), source: 'energy' }));

  const fromDiscipline = (disciplineSeries || []).map((p) => ({ ...p, source: 'discipline' }));

  // Merge by date (discipline wins when both exist), sort, take last 14.
  const byDate = new Map();
  for (const p of fallbackFromEnergy) byDate.set(p.date, p);
  for (const p of fromDiscipline)     byDate.set(p.date, p); // override
  const points = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date)).slice(-14);
  const max = Math.max(100, ...points.map((p) => p.score));
  const usingFallback = points.length > 0 && points.every((p) => p.source === 'energy');

  return (
    <GlowCard testId="discipline-phase-chart" className="p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-fuchsia-300"
          style={{ textShadow: '0 0 6px rgba(232,121,249,0.6)' }}>
          {fr ? 'Discipline × Cycle (14j)' : 'Discipline × Cycle (14d)'}
        </p>
        <span className="text-[10px] font-mono text-muted-foreground/70" data-testid="discipline-chart-count">{points.length}/14</span>
      </div>
      <div className="flex items-end gap-1.5 h-28">
        {points.length === 0
          ? <p className="text-xs text-muted-foreground italic">{fr ? 'Logue ton premier jour pour voir le graphique' : 'Log your first day to see the chart'}</p>
          : points.map((p) => {
              const h = Math.max(6, Math.round((p.score / max) * 100));
              const isPeriod = periodDays.has(p.date);
              return (
                <div key={p.date} className="flex-1 flex flex-col items-center gap-1" title={`${p.date} · ${p.score} · ${p.source}`}>
                  <div className="w-full rounded-t-sm transition-all"
                    style={{
                      height: `${h}%`,
                      background: isPeriod
                        ? 'linear-gradient(180deg, #f43f5e, #be123c)'
                        : 'linear-gradient(180deg, #c084fc, #7e22ce)',
                      boxShadow: isPeriod
                        ? '0 0 8px rgba(244,63,94,0.7)'
                        : '0 0 8px rgba(192,132,252,0.65)',
                    }} />
                  <span className="text-[8px] font-mono text-muted-foreground/60">{p.date.slice(8)}</span>
                </div>
              );
            })}
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px] uppercase tracking-widest flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-fuchsia-300/85"><span className="w-2 h-2 rounded-full bg-fuchsia-400" />{usingFallback ? (fr ? 'Énergie loguée' : 'Logged energy') : (fr ? 'Discipline' : 'Discipline')}</span>
        <span className="inline-flex items-center gap-1.5 text-rose-300/85"><span className="w-2 h-2 rounded-full bg-rose-400" />{fr ? 'Jour règles' : 'Period'}</span>
      </div>
    </GlowCard>
  );
}

// ─── Educational tabs ───
// "Extra Miles" expert content — structured sections with minimalist icons.
// Each phase has 3-4 sub-sections (icon + heading + body), not flat bullets.
const PHASES_OF_LIFE = [
  {
    id: 'cycle_actif',
    icon: Sparkles,
    title_fr: 'Cycle Régulier',
    title_en: 'Regular Cycle',
    sub_fr: 'Œstrogène J1-14 · Progestérone J15-28',
    sub_en: 'Estrogen D1-14 · Progesterone D15-28',
    sections_fr: [
      {
        icon: Dumbbell,
        title: "Œstrogène & force (J1-J14)",
        body: "L'œstrogène augmente jour après jour jusqu'à l'ovulation. C'est ton MEILLEUR moment pour gagner en force : récupération neuro plus rapide, tendons plus élastiques, sensibilité à l'insuline au max. Charge tes squats, deadlifts, presses lourdes — vise les PR entre J10 et J14. Glucides complexes (riz, patates douces, avoine) au matin et autour du training.",
      },
      {
        icon: Flame,
        title: "Ovulation J14 — peak vitality",
        body: "Le pic œstrogénique te donne la confiance, l'énergie, la libido, le focus. Active les missions importantes ce jour-là (présentation, gros entraînement, décision stratégique). Ton cerveau verbal est aussi à son meilleur — c'est le moment des conversations difficiles.",
      },
      {
        icon: Coffee,
        title: "Progestérone J15-J28 — chaleur & faim",
        body: "Après l'ovulation, la progestérone monte. Conséquences concrètes : température corporelle +0.3-0.5°C, fréquence cardiaque +5-8 bpm, métabolisme +5-10% (donc oui, tu manges plus — c'est physiologique, pas un manque de discipline). Augmente les protéines de 10g/repas pour stabiliser la glycémie. Magnésium glycinate 300-400mg le soir.",
      },
      {
        icon: Moon,
        title: "Pré-règles J25-J28 — passe en mode récup",
        body: "Inflammation systémique en hausse, sommeil souvent perturbé. Baisse ton volume cardio de 20%, garde la force (mais avec des charges 5-10% plus légères et plus de reps), évite l'alcool et les sucres rapides qui amplifient le SPM. Priorité absolue : 8h de sommeil, chambre fraîche.",
      },
    ],
    sections_en: [
      { icon: Dumbbell, title: 'Estrogen & strength (D1-D14)', body: "Estrogen rises until ovulation. This is your BEST window for strength gains: faster neuro recovery, more elastic tendons, peak insulin sensitivity. Load up squats, deadlifts, heavy press — aim for PRs between D10 and D14. Complex carbs morning + around training." },
      { icon: Flame,    title: 'Ovulation D14 — peak vitality', body: 'The estrogen spike brings confidence, energy, libido, focus. Schedule important missions today (presentation, big workout, strategic decision). Verbal brain is also at its best — time for hard conversations.' },
      { icon: Coffee,   title: 'Progesterone D15-D28 — heat & hunger', body: 'After ovulation, progesterone rises. Real effects: body temp +0.3-0.5°C, HR +5-8bpm, metabolism +5-10% (yes you eat more — physiological, not weak discipline). Add 10g protein per meal to stabilize glycemia. Magnesium glycinate 300-400mg at night.' },
      { icon: Moon,     title: 'Pre-period D25-D28 — switch to recovery', body: 'Systemic inflammation rises, sleep is often disturbed. Drop cardio volume 20%, keep strength (but charges 5-10% lighter, more reps), avoid alcohol and quick sugar which amplify PMS. Top priority: 8h sleep, cool room.' },
    ],
  },
  {
    id: 'perimenopause',
    icon: Activity,
    title_fr: 'Périménopause',
    title_en: 'Perimenopause',
    sub_fr: 'Insulino-résistance · Force lourde · Cortisol',
    sub_en: 'Insulin resistance · Heavy lifting · Cortisol',
    sections_fr: [
      {
        icon: Wheat,
        title: "Insulino-résistance naissante",
        body: "Avec la baisse progressive de l'œstrogène, tes cellules deviennent moins sensibles à l'insuline. Conséquence : graisse abdominale qui s'installe plus vite, fringales sucrées en fin d'après-midi. Stratégie : sépare tes glucides — privilégie-les autour de l'entraînement (1h avant + 1h après), baisse-les le soir. Vinaigre de cidre 1 c.à.s. avant les repas riches en glucides aide à la régulation glycémique.",
      },
      {
        icon: Dumbbell,
        title: "Lever lourd — non négociable",
        body: "C'est ta première arme contre la baisse hormonale. La force lourde (5x5 squat, deadlift, press, row à 75-85% de ton 1RM) stimule la testostérone résiduelle, préserve la masse maigre et la densité osseuse. 3 séances/semaine de force pure > 5 séances de cardio. Ton corps répond mieux à la rigueur qu'à la quantité.",
      },
      {
        icon: ShieldCheck,
        title: "Cortisol — l'ennemi silencieux",
        body: "Le cortisol monte plus vite et redescend moins bien qu'avant. Cardio long (>45 min) à intensité moyenne = stress chronique. Préfère le HIIT court (8-12 min, 3x/semaine) + marche quotidienne. Méditation 10 min/jour, chambre à 18-19°C, ZÉRO écran 90 min avant le coucher.",
      },
      {
        icon: Clock,
        title: "Timing protéines",
        body: "1.4-1.6 g/kg/jour répartis sur 4 repas (~30g par repas minimum). La synthèse protéique est moins efficace, donc on compense par la fréquence. Whey ou caséine 30g avant le coucher améliore la récupération nocturne et stabilise la glycémie.",
      },
    ],
    sections_en: [
      { icon: Wheat,       title: 'Emerging insulin resistance',  body: "As estrogen drops gradually, cells become less insulin-sensitive. Result: abdominal fat sets in faster, late-afternoon sugar cravings. Strategy: split carbs — prioritize them around training (1h pre + 1h post), lower them at night. Apple cider vinegar 1 tbsp before high-carb meals helps glycemic control." },
      { icon: Dumbbell,    title: 'Heavy lifting — non-negotiable', body: 'Your #1 weapon against hormonal decline. Heavy strength (5x5 squat, deadlift, press, row at 75-85% 1RM) stimulates residual testosterone, preserves lean mass and bone density. 3 strength sessions/week > 5 cardio. Body responds better to rigor than quantity.' },
      { icon: ShieldCheck, title: 'Cortisol — the silent enemy', body: 'Cortisol rises faster and clears slower. Long cardio (>45min) mid-intensity = chronic stress. Prefer short HIIT (8-12min, 3x/week) + daily walks. 10min meditation, 18-19°C room, ZERO screens 90min before bed.' },
      { icon: Clock,       title: 'Protein timing',  body: '1.4-1.6 g/kg/day split across 4 meals (~30g per meal min). Protein synthesis is less efficient — compensate with frequency. 30g whey or casein at bedtime improves overnight recovery and glycemic stability.' },
    ],
  },
  {
    id: 'menopause',
    icon: Heart,
    title_fr: 'Ménopause',
    title_en: 'Menopause',
    sub_fr: 'Protéines 1.4-1.8g/kg · Os · Force',
    sub_en: 'Protein 1.4-1.8g/kg · Bones · Strength',
    sections_fr: [
      {
        icon: Egg,
        title: "Protéines — priorité absolue",
        body: "Ratio 1.4-1.8 g/kg/jour minimum, idéalement 4 prises de 30-40g (œufs, poulet, poisson, whey, légumineuses + céréales complètes). En dessous de ce seuil, ton corps cannibalise le muscle pour son énergie. Avec ce seuil, il préserve et même reconstruit. C'est mathématique.",
      },
      {
        icon: Bone,
        title: "Santé osseuse — la décennie critique",
        body: "Sans œstrogène, la perte osseuse accélère (1-2% par an les 5 premières années). Triple combo : Calcium 1200mg + Vitamine D3 2000-4000 UI + K2 (MK-7) 100µg/jour. Cofacteurs : magnésium 400mg, bore 3mg. Marche en charge (avec sac à dos lesté) ou musculation = stimulus mécanique indispensable pour les ostéoblastes.",
      },
      {
        icon: Dumbbell,
        title: "Force lourde 3x/semaine",
        body: "Non négociable. Squat, deadlift, soulevé de terre, push-press, rows. Charges progressives. Pas de \"trop dangereux à mon âge\" — c'est l'inverse : c'est PARCE QUE tu vieillis que tu DOIS lever lourd. La fragilité tue, la force protège.",
      },
      {
        icon: Salad,
        title: "Inflammation & cognition",
        body: "Oméga-3 (DHA/EPA) 2-3 g/jour — santé cardio, cognition, bouffées de chaleur. Curcuma 500mg + poivre noir avec graisse, à un repas. Hydratation +20% (la peau perd en élasticité, les muqueuses aussi). Café max 1 le matin pour ne pas perturber le sommeil déjà fragile.",
      },
    ],
    sections_en: [
      { icon: Egg,      title: 'Protein — absolute priority', body: 'Ratio 1.4-1.8 g/kg/day min, ideally 4 portions of 30-40g (eggs, chicken, fish, whey, legumes + whole grains). Below this threshold, body cannibalizes muscle for energy. At/above, it preserves and rebuilds. Pure math.' },
      { icon: Bone,     title: 'Bone health — the critical decade', body: 'Without estrogen, bone loss accelerates (1-2%/yr first 5 years). Triple combo: Calcium 1200mg + Vit D3 2000-4000 IU + K2 (MK-7) 100µg/day. Cofactors: magnesium 400mg, boron 3mg. Loaded walking (weighted vest) or lifting = mechanical stimulus required for osteoblasts.' },
      { icon: Dumbbell, title: 'Heavy strength 3x/week', body: "Non-negotiable. Squat, deadlift, push-press, rows. Progressive load. No \"too dangerous at my age\" — opposite: it's BECAUSE you're aging that you MUST lift heavy. Fragility kills, strength protects." },
      { icon: Salad,    title: 'Inflammation & cognition', body: 'Omega-3 (DHA/EPA) 2-3g/day — cardio, cognition, hot flashes. Curcumin 500mg + black pepper with fat, at one meal. Hydration +20% (skin loses elasticity, mucous too). Coffee max 1 morning to protect already-fragile sleep.' },
    ],
  },
];

function EducationalTabs({ fr }) {
  const [tab, setTab] = useState('cycle_actif');
  const active = PHASES_OF_LIFE.find((p) => p.id === tab) || PHASES_OF_LIFE[0];
  const Icon = active.icon;
  return (
    <GlowCard testId="education-tabs" className="p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-fuchsia-300"
          style={{ textShadow: '0 0 6px rgba(232,121,249,0.6)' }}>
          {fr ? "Centre éducatif" : 'Educational center'}
        </p>
      </div>
      <div className="flex gap-1.5 mb-5 flex-wrap">
        {PHASES_OF_LIFE.map((p) => (
          <button key={p.id} onClick={() => setTab(p.id)} data-testid={`edu-tab-${p.id}`}
            className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
              tab === p.id
                ? 'bg-gradient-to-r from-fuchsia-500/30 to-purple-500/30 text-fuchsia-200 ring-1 ring-fuchsia-400/55'
                : 'bg-zinc-900/70 text-muted-foreground ring-1 ring-zinc-800 hover:ring-fuchsia-500/35'
            }`}
            style={tab === p.id ? { boxShadow: '0 0 10px rgba(192,132,252,0.45)' } : {}}>
            {fr ? p.title_fr : p.title_en}
          </button>
        ))}
      </div>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-fuchsia-500/15 flex items-center justify-center flex-shrink-0"
          style={{ boxShadow: '0 0 12px rgba(192,132,252,0.4)' }}>
          <Icon className="w-5 h-5 text-fuchsia-300" />
        </div>
        <div>
          <h3 className="font-heading text-lg uppercase tracking-tight font-black text-foreground">
            {fr ? active.title_fr : active.title_en}
          </h3>
          <p className="text-xs text-muted-foreground">{fr ? active.sub_fr : active.sub_en}</p>
        </div>
      </div>
      <ul className="space-y-3.5" data-testid="education-sections">
        {(fr ? active.sections_fr : active.sections_en).map((s, i) => {
          const SI = s.icon;
          return (
            <li key={i} className="flex items-start gap-3" data-testid={`edu-section-${active.id}-${i}`}>
              <div
                className="w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500/20 to-purple-500/15 flex items-center justify-center flex-shrink-0 ring-1 ring-fuchsia-400/35"
                style={{ boxShadow: '0 0 10px rgba(192,132,252,0.4)' }}
                aria-hidden="true"
              >
                <SI className="w-4 h-4 text-fuchsia-300" style={{ filter: 'drop-shadow(0 0 5px rgba(232,121,249,0.7))' }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.18em] font-black text-fuchsia-200 mb-1"
                  style={{ textShadow: '0 0 6px rgba(232,121,249,0.45)' }}>
                  {s.title}
                </p>
                <p className="text-sm text-foreground/85 leading-relaxed">{s.body}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </GlowCard>
  );
}

// ─── AI Insight ribbon (uses cycle.phase + symptoms) ───
function AIInsight({ cycle, logs, fr }) {
  if (!cycle?.active) return null;
  const meta = PHASE_PALETTE[cycle.phase] || PHASE_PALETTE.follicular;
  const recentFatigue = logs.slice(-3).filter((l) => l.symptoms?.fatigue).length;
  const recentCravings = logs.slice(-3).filter((l) => l.symptoms?.sugar_cravings).length;

  let text;
  if (cycle.phase === 'luteal' || cycle.phase === 'pre_menstrual') {
    text = fr
      ? "Tu es en phase lutéale. Augmente le magnésium (oléagineux, chocolat noir 80%+), baisse l'intensité cardio de ~10% aujourd'hui, vise 10g de protéines en plus pour stabiliser la glycémie."
      : 'You are in luteal phase. Increase magnesium (nuts, dark chocolate 80%+), drop cardio intensity ~10% today, add ~10g protein to stabilize blood sugar.';
  } else if (cycle.phase === 'menstrual') {
    text = fr
      ? 'Phase menstruelle : repos actif (mobilité, marche), priorise le fer (foie, viande rouge maigre, lentilles + vitamine C) et magnésium pour les crampes.'
      : 'Menstrual phase: active rest (mobility, walks), prioritize iron (liver, lean red meat, lentils + vitamin C) and magnesium for cramps.';
  } else if (cycle.phase === 'ovulation') {
    text = fr
      ? "Pic d'énergie : c'est le moment des PR. Glucides complexes 1h30 avant l'entraînement, focus force lourde + sprint."
      : 'Energy peak: PR window. Complex carbs 90min pre-workout, focus heavy strength + sprints.';
  } else {
    text = fr
      ? 'Phase folliculaire : insulino-sensibilité élevée → tu peux pousser le cardio HIIT, glucides complexes OK matin et midi.'
      : 'Follicular phase: high insulin sensitivity → push HIIT cardio, complex carbs OK morning + lunch.';
  }
  if (recentFatigue >= 2) {
    text += fr ? " (Fatigue récurrente détectée — vérifie ton apport en fer + sommeil 8h+.)" : ' (Recurring fatigue detected — check iron intake + 8h+ sleep.)';
  }
  if (recentCravings >= 2) {
    text += fr ? " Rage de sucre récurrente : ajoute des protéines aux 3 repas, baisse le café après 14h." : ' Recurring sugar cravings: add protein to all 3 meals, cut coffee after 2pm.';
  }
  return (
    <GlowCard testId="ai-insight" className="p-4 sm:p-5" accent={meta.color}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${meta.color}22`, boxShadow: `0 0 10px ${meta.color}55` }}>
          <Brain className="w-4 h-4" style={{ color: meta.color }} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1" style={{ color: meta.color, textShadow: `0 0 6px ${meta.color}99` }}>
            {fr ? 'Recommandation IA' : 'AI recommendation'}
          </p>
          <p className="text-sm text-foreground/90 leading-relaxed" data-testid="ai-insight-text">{text}</p>
        </div>
      </div>
    </GlowCard>
  );
}

// ─── Page root ───
const FemaleHealth = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');

  const [overview, setOverview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorDate, setEditorDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  // Cheap client-side gate to avoid noisy 403s in DevTools when a male user
  // hits this route directly. Fetched from /auth/me on mount. Staff (coach,
  // admin, super_admin) is allowed regardless of biological_sex.
  const [meProfile, setMeProfile] = useState(null); // { biological_sex, role }
  useEffect(() => {
    injectFemaleHealthKeyframes();
    let alive = true;
    api.get('/auth/me')
      .then((r) => {
        if (!alive) return;
        setMeProfile({
          biological_sex: r.data?.biological_sex || null,
          role: r.data?.role || null,
        });
      })
      .catch(() => {
        if (alive) setMeProfile({ biological_sex: null, role: null });
      });
    return () => { alive = false; };
  }, []);

  const todayStr = ymd(new Date());

  // Load overview on mount — only after we know the user is female (or staff role).
  useEffect(() => {
    if (meProfile === null) return; // still loading /users/me
    const isStaff = ['coach', 'admin', 'super_admin'].includes(meProfile.role);
    if (meProfile.biological_sex !== 'femme' && !isStaff) {
      // Show forbidden screen without firing any /female-health fetches.
      setForbidden(true);
      setLoading(false);
      return;
    }
    let alive = true;
    api.get('/female-health/overview')
      .then((r) => {
        if (!alive) return;
        setOverview(r.data);
        setLogs(r.data?.logs || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        if (e?.response?.status === 403) setForbidden(true);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [meProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load logs for the selected month when it changes (only when allowed).
  useEffect(() => {
    if (forbidden || meProfile === null) return;
    let alive = true;
    const from = ymd(startOfMonth(currentMonth));
    const to   = ymd(endOfMonth(currentMonth));
    api.get(`/female-health/logs?from_=${from}&to=${to}`)
      .then((r) => {
        if (!alive) return;
        // Merge with existing (don't lose other months)
        setLogs((prev) => {
          const filtered = prev.filter((l) => l.log_date < from || l.log_date > to);
          return [...filtered, ...(r.data?.logs || [])].sort((a, b) => a.log_date.localeCompare(b.log_date));
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [currentMonth, forbidden, meProfile]);

  const logsByDate = useMemo(() => {
    const m = {};
    for (const l of logs) m[l.log_date] = l;
    return m;
  }, [logs]);

  const handlePickDay = (ds) => { setEditorDate(ds); setEditorOpen(true); };
  const handleSaved = (saved) => {
    if (!saved) return;
    setLogs((prev) => {
      const idx = prev.findIndex((l) => l.log_date === saved.log_date);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [...prev, saved].sort((a, b) => a.log_date.localeCompare(b.log_date));
    });
  };

  if (forbidden) {
    return (
      <Layout>
        <div className="p-6 max-w-2xl mx-auto" data-testid="female-health-forbidden">
          <h1 className="font-heading text-xl uppercase tracking-tight text-foreground mb-2">
            {fr ? 'Module réservé' : 'Restricted module'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {fr ? 'Ce module est réservé aux profils féminins. Si tu penses qu\'il s\'agit d\'une erreur, va dans Réglages → Profil santé pour vérifier ton sexe biologique.' : 'This module is reserved for female profiles.'}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="female-health-page">
        {/* Header */}
        <div className="mb-6">
          <MauveLabel testId="female-health-label" color="#c084fc">
            {fr ? 'Santé Féminine · Mode Premium' : 'Female Health · Premium Mode'}
          </MauveLabel>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-foreground mt-3">
            {fr ? 'Ton Cycle, ' : 'Your Cycle, '}
            <span className="bg-gradient-to-r from-fuchsia-400 via-purple-400 to-rose-400 bg-clip-text text-transparent">
              {fr ? 'En Lumière.' : 'In Light.'}
            </span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            {fr
              ? "Croise ton cycle avec ta discipline et ton énergie. Édite n'importe quel jour passé. L'IA adapte tes recommandations en temps réel."
              : 'Cross your cycle with discipline and energy. Edit any past day. The AI adapts in real time.'}
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">{fr ? 'Chargement…' : 'Loading…'}</p>
        ) : (
          <div className="space-y-5">
            {/* Phase ring */}
            <PhaseRing cycle={overview?.cycle} fr={fr} />

            {/* AI Insight */}
            <AIInsight cycle={overview?.cycle} logs={logs} fr={fr} />

            {/* Live tracking : calendar + cross chart */}
            <div className="grid lg:grid-cols-2 gap-5">
              <CycleCalendar logsByDate={logsByDate} currentMonth={currentMonth}
                setCurrentMonth={setCurrentMonth} onPickDay={handlePickDay} fr={fr} todayStr={todayStr} />
              <DisciplineXPhaseChart disciplineSeries={overview?.discipline_series || []} logs={logs} fr={fr} />
            </div>

            {/* Quick action: log today — animated glow on hover (Yuri spec 2026-02 Iter. 314) */}
            <button
              type="button"
              data-testid="female-health-log-today"
              onClick={() => handlePickDay(todayStr)}
              className="fh-log-today-btn group relative w-full overflow-hidden text-white font-black uppercase tracking-widest text-xs py-5 rounded-full"
              style={{
                background: 'linear-gradient(90deg, #d946ef 0%, #a855f7 50%, #d946ef 100%)',
                backgroundSize: '200% 100%',
                boxShadow: '0 0 22px rgba(192,132,252,0.55)',
                animation: 'fh-log-shimmer 6s linear infinite',
              }}
            >
              {/* Pulsing aura on hover */}
              <span aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  boxShadow: '0 0 36px rgba(232,121,249,0.95), 0 0 12px rgba(192,132,252,0.85), inset 0 0 0 1px rgba(255,255,255,0.18)',
                  animation: 'fh-log-pulse 1.6s ease-in-out infinite',
                }}
              />
              <span className="relative inline-flex items-center justify-center gap-2 group-hover:scale-105 transition-transform duration-200">
                <Pencil className="w-4 h-4" />
                {fr ? "Loguer aujourd'hui" : 'Log today'}
              </span>
            </button>

            {/* Educational center */}
            <EducationalTabs fr={fr} />

            {/* Footer help */}
            <div className="text-center pt-2">
              <button data-testid="female-health-settings"
                onClick={() => navigate('/settings')}
                className="text-[11px] uppercase tracking-widest text-muted-foreground hover:text-fuchsia-300 transition-colors">
                {fr ? 'Modifier mon profil hormonal →' : 'Edit hormonal profile →'}
              </button>
            </div>
          </div>
        )}

        <AnimatePresence>
          {editorOpen && (
            <DayLogEditor open={editorOpen} onClose={() => setEditorOpen(false)}
              dateStr={editorDate} log={logsByDate[editorDate]} onSaved={handleSaved} fr={fr} />
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
};

export default FemaleHealth;
